import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import PlanActions from "@/components/PlanActions";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Users, TrendingUp, Edit, UserPlus, Target, User, Plus, Trash2, Crown, BriefcaseBusiness, Mic } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { fetchLatestScores } from "@/lib/personaScore";
import { PersonaScoreBadge } from "@/components/PersonaScoreBadge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VoucherCard } from "@/components/VoucherCard";

const Coordenador = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [empresa, setEmpresa] = useState<any>(null);
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [personas, setPersonas] = useState<any[]>([]);
  const [personaScores, setPersonaScores] = useState<Record<string, number>>({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [novoUsuarioDialogOpen, setNovoUsuarioDialogOpen] = useState(false);
  const [companyDetails, setCompanyDetails] = useState({
    nome: "",
    produto: "",
    publico: "",
    proposta_valor: "",
  });
  const [novoUsuario, setNovoUsuario] = useState({
    nome: "",
    email: "",
    papel: "vendedor",
  });

  useEffect(() => {
    const checkAccess = async () => {
      if (authLoading) return;

      if (!user) {
        navigate("/auth");
        return;
      }

      try {
        const { data: usuario } = await supabase
          .from("usuarios")
          .select("papel, empresa_id")
          .eq("id", user.id)
          .single();

        if (!usuario || usuario.papel !== "coordenador") {
          navigate("/dashboard");
          return;
        }

        if (!usuario.empresa_id) {
          navigate("/briefing");
          return;
        }

        // Load empresa data
        const { data: empresaData } = await supabase.from("empresas").select("*").eq("id", usuario.empresa_id).single();

        if (empresaData) {
          setEmpresa(empresaData);
          setCompanyDetails({
            nome: empresaData.nome || "",
            produto: empresaData.produto || "",
            publico: empresaData.publico || "",
            proposta_valor: empresaData.proposta_valor || "",
          });

          // Verificar voucher pendente do signup e resgatar
          const pendingVoucher = localStorage.getItem('pending_voucher');
          if (pendingVoucher) {
            localStorage.removeItem('pending_voucher');
            try {
              const { data: rdata, error: rerr } = await supabase.functions.invoke('redeem-voucher', {
                body: { codigo: pendingVoucher },
              });
              if (!rerr && !(rdata as any)?.error) {
                toast({
                  title: 'Voucher resgatado!',
                  description: `Plano ${(rdata as any).plano === 'expert' ? 'Expert' : 'Max Pro'} ativado.`,
                });
                // Recarregar empresa com novo plano
                const { data: refreshed } = await supabase.from("empresas").select("*").eq("id", usuario.empresa_id).single();
                if (refreshed) setEmpresa(refreshed);
              } else {
                toast({
                  title: 'Voucher inválido',
                  description: (rdata as any)?.error || 'Verifique o código informado.',
                  variant: 'destructive',
                });
              }
            } catch (_e) { /* silencioso */ }
          }

          // Se voucher expirou, dispara verificação (envia email se ainda não enviado)
          if (
            empresaData.voucher_id &&
            empresaData.plano_expira_em &&
            new Date(empresaData.plano_expira_em) < new Date() &&
            !empresaData.voucher_expirado_email_enviado_em
          ) {
            supabase.functions.invoke('check-vouchers-expirados', {
              body: { empresa_id: usuario.empresa_id },
            }).catch(() => {});
          }

          // Load usuarios da empresa (com status pendente via edge function)
          const { data: teamData } = await supabase.functions.invoke("list-team", {
            body: { empresa_id: usuario.empresa_id },
          });
          if (teamData?.usuarios) {
            setUsuarios(teamData.usuarios);
          }

          // Load personas da empresa
          const { data: personasData } = await supabase
            .from("personas")
            .select("*")
            .eq("empresa_id", usuario.empresa_id)
            .order("created_at", { ascending: false });

          if (personasData) {
            setPersonas(personasData);
            const scores = await fetchLatestScores(personasData.map((p: any) => p.id));
            setPersonaScores(scores);
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [user, authLoading, navigate]);

  const handleSaveEmpresa = async () => {
    if (!empresa) return;

    try {
      const { error } = await supabase
        .from("empresas")
        .update({
          nome: companyDetails.nome,
          produto: companyDetails.produto,
          publico: companyDetails.publico,
          proposta_valor: companyDetails.proposta_valor,
        })
        .eq("id", empresa.id);

      if (error) throw error;

      toast({
        title: "Sucesso!",
        description: "Informações da empresa atualizadas.",
      });
      setEditDialogOpen(false);
      setEmpresa({ ...empresa, ...companyDetails });
    } catch (error) {
      console.error("Error updating empresa:", error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar as informações.",
        variant: "destructive",
      });
    }
  };

  const carregarDados = async () => {
    if (!user) return;

    const { data: usuario } = await supabase.from("usuarios").select("empresa_id").eq("id", user.id).single();

    if (!usuario?.empresa_id) return;

    // Recarregar usuários via edge function (com status pendente)
    const { data: teamData } = await supabase.functions.invoke("list-team", {
      body: { empresa_id: usuario.empresa_id },
    });
    if (teamData?.usuarios) {
      setUsuarios(teamData.usuarios);
    }

    // Recarregar personas
    const { data: personasData } = await supabase
      .from("personas")
      .select("*")
      .eq("empresa_id", usuario.empresa_id)
      .order("created_at", { ascending: false });

    if (personasData) {
      setPersonas(personasData);
    }

    // Recarregar empresa
    const { data: empresaData } = await supabase.from("empresas").select("*").eq("id", usuario.empresa_id).single();

    if (empresaData) {
      setEmpresa(empresaData);
    }
  };

  const handleAddUser = async () => {
    if (!novoUsuario.nome || !novoUsuario.email || !empresa?.id) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos.",
        variant: "destructive",
      });
      return;
    }

    const maxUsers = empresa?.plano === "start" ? 1 : empresa?.plano === "expert" ? 5 : Number.POSITIVE_INFINITY;

    if (usuarios.length >= maxUsers) {
      toast({
        title: "Limite atingido",
        description: `Limite de usuários atingido para o plano ${empresa?.plano}. Faça upgrade para adicionar mais.`,
        variant: "destructive",
      });
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("invite-usuario", {
        body: {
          nome: novoUsuario.nome,
          email: novoUsuario.email,
          papel: novoUsuario.papel,
          empresa_id: empresa.id,
        },
      });

      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      toast({
        title: "Convite enviado!",
        description: "O usuário receberá um e-mail para definir a senha e acessar a plataforma.",
      });

      setNovoUsuarioDialogOpen(false);
      setNovoUsuario({ nome: "", email: "", papel: "vendedor" });
      carregarDados();
    } catch (error: any) {
      console.error("Erro ao convidar usuário:", error);
      toast({
        title: "Erro",
        description: "Erro ao convidar usuário: " + (error?.message || "desconhecido"),
        variant: "destructive",
      });
    }
  };

  const tornarCoordenador = async (userId: string) => {
    try {
      const { error } = await supabase.from("usuarios").update({ papel: "coordenador" }).eq("id", userId);

      if (error) throw error;

      toast({
        title: "Sucesso!",
        description: "Usuário promovido a coordenador.",
      });

      carregarDados();
    } catch (error) {
      console.error("Erro ao tornar coordenador:", error);
      toast({
        title: "Erro",
        description: "Não foi possível promover o usuário.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteUsuario = async (usuarioId: string) => {
    try {
      const { error } = await supabase.from("usuarios").delete().eq("id", usuarioId);

      if (error) throw error;

      toast({
        title: "Usuário excluído",
        description: "O usuário foi excluído com sucesso.",
      });

      carregarDados();
    } catch (error) {
      console.error("Erro ao excluir usuário:", error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o usuário.",
        variant: "destructive",
      });
    }
  };

  const handleDeletePersona = async (personaId: string) => {
    try {
      const { error } = await supabase.from("personas").delete().eq("id", personaId);

      if (error) throw error;

      toast({
        title: "Persona excluída",
        description: "A persona foi excluída com sucesso.",
      });

      carregarDados();
    } catch (error) {
      console.error("Erro ao excluir persona:", error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir a persona.",
        variant: "destructive",
      });
    }
  };

  const handleStartTraining = () => {
    const limite = empresa?.plano === "start" ? 10 : Number.POSITIVE_INFINITY;
    const podeCriar = personas.length < limite;

    if (!podeCriar) {
      toast({
        title: "Limite atingido",
        description: `Limite de personas atingido para o plano ${empresa?.plano}. Faça upgrade para criar mais personas.`,
        variant: "destructive",
      });
      return;
    }

    navigate(`/treinamento?empresaId=${empresa?.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Layout title="Coordenador">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Painel do Coordenador</h1>
            <p className="text-muted-foreground mt-2">Gerencie sua equipe e acompanhe o desempenho</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Edit className="w-4 h-4 mr-2" />
                  Editar Empresa
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Editar Informações da Empresa</DialogTitle>
                  <DialogDescription>Atualize as informações da sua empresa</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="nome">Nome da Empresa</Label>
                    <Input
                      id="nome"
                      value={companyDetails.nome}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, nome: e.target.value })}
                      placeholder="Nome da sua empresa"
                    />
                  </div>
                  <div>
                    <Label htmlFor="produto">Produto/Serviço</Label>
                    <Textarea
                      id="produto"
                      value={companyDetails.produto}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, produto: e.target.value })}
                      placeholder="Descreva seu produto ou serviço"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="publico">Público-alvo</Label>
                    <Textarea
                      id="publico"
                      value={companyDetails.publico}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, publico: e.target.value })}
                      placeholder="Descreva seu público-alvo"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label htmlFor="proposta">Proposta de Valor</Label>
                    <Textarea
                      id="proposta"
                      value={companyDetails.proposta_valor}
                      onChange={(e) => setCompanyDetails({ ...companyDetails, proposta_valor: e.target.value })}
                      placeholder="Qual é sua proposta de valor?"
                      rows={3}
                    />
                  </div>
                  <Button onClick={handleSaveEmpresa} className="w-full">
                    Salvar Alterações
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={novoUsuarioDialogOpen} onOpenChange={setNovoUsuarioDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  onClick={(e) => {
                    const maxUsers =
                      empresa?.plano === "start" ? 1 : empresa?.plano === "expert" ? 5 : Number.POSITIVE_INFINITY;

                    if (empresa?.plano === "start" || (empresa?.plano === "expert" && usuarios.length >= maxUsers)) {
                      e.preventDefault();
                      navigate("/upgrade");
                    }
                  }}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Cadastrar Usuário
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Novo Usuário</DialogTitle>
                  <DialogDescription>Adicionar novo usuário à empresa</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="novo-nome">Nome</Label>
                    <Input
                      id="novo-nome"
                      value={novoUsuario.nome}
                      onChange={(e) => setNovoUsuario({ ...novoUsuario, nome: e.target.value })}
                      placeholder="Nome do usuário"
                    />
                  </div>
                  <div>
                    <Label htmlFor="novo-email">Email</Label>
                    <Input
                      id="novo-email"
                      type="email"
                      value={novoUsuario.email}
                      onChange={(e) => setNovoUsuario({ ...novoUsuario, email: e.target.value })}
                      placeholder="email@exemplo.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="novo-papel">Papel</Label>
                    <Select
                      value={novoUsuario.papel}
                      onValueChange={(value) => setNovoUsuario({ ...novoUsuario, papel: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um papel..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="vendedor">Vendedor</SelectItem>
                        <SelectItem value="coordenador">Coordenador</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleAddUser} className="w-full">
                    Criar Usuário
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Equipe
              </CardTitle>
              <CardDescription>Membros da sua empresa</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{usuarios.length}</p>
              <p className="text-sm text-muted-foreground">usuários cadastrados</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Feedbacks
              </CardTitle>
              <CardDescription>Feedbacks disponíveis</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{empresa?.feedbacks || 0}</p>
              <p className="text-sm text-muted-foreground">feedbacks restantes</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5" />
                Plano
              </CardTitle>
              <CardDescription>Plano atual</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-2xl font-bold capitalize">{empresa?.plano || "Start"}</p>
                <p className="text-sm text-muted-foreground">plano ativo</p>
              </div>
              {empresa?.plano !== "max_pro" && (
                <Button onClick={() => navigate("/upgrade")} variant="outline" className="w-full">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Fazer Upgrade
                </Button>
              )}
              {empresa?.plano && empresa.plano !== "start" && (
                <PlanActions onChanged={carregarDados} />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Card do Voucher */}
        {empresa && (
          <VoucherCard empresa={empresa} onApplied={carregarDados} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-card to-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Treinamento
              </CardTitle>
              <CardDescription>
                {(() => {
                  const limite = empresa?.plano === "start" ? 10 : Number.POSITIVE_INFINITY;
                  const podeCriar = personas.length < limite;
                  return podeCriar
                    ? "Iniciar novo treinamento com persona"
                    : `Limite de ${limite} personas atingido para o plano ${empresa?.plano}`;
                })()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleStartTraining} className="w-full" size="lg">
                <Plus className="w-4 h-4 mr-2" />
                Iniciar Treinamento com Nova Persona
              </Button>
            </CardContent>
          </Card>

          <Card className="border-2 border-primary/30 bg-gradient-to-br from-card via-card to-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BriefcaseBusiness className="w-5 h-5 text-primary" />
                Auxiliar de Venda em Tempo Real
              </CardTitle>
              <CardDescription>
                Acompanhe as sessões da equipe, veja o Kanban de 6 etapas e relatórios de IA
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate("/auxiliar-vendas")} className="w-full" size="lg" variant="outline">
                <Mic className="w-4 h-4 mr-2 text-primary" />
                Abrir Auxiliar de Venda
              </Button>
            </CardContent>
          </Card>
        </div>

        <div>
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Users className="w-6 h-6" />
            Usuários da Empresa
          </h2>
          {usuarios.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {usuarios.map((usuario) => (
                <Card key={usuario.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <CardTitle className="text-lg truncate">{usuario.nome || "Sem nome"}</CardTitle>
                        <CardDescription className="truncate">{usuario.email}</CardDescription>
                      </div>
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full whitespace-nowrap ${
                          usuario.is_pendente
                            ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                            : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                        }`}
                      >
                        {usuario.is_pendente ? "Convite pendente" : "Ativo"}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Papel:</span>
                      <span className="text-sm font-medium capitalize">{usuario.papel}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Cadastro:</span>
                      <span className="text-sm">{new Date(usuario.created_at).toLocaleDateString("pt-BR")}</span>
                    </div>
                    <div className="flex gap-2 mt-2">
                      {usuario.papel !== "coordenador" && (
                        <Button
                          onClick={() => tornarCoordenador(usuario.id)}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                        >
                          <Crown className="w-4 h-4 mr-2" />
                          Tornar Coordenador
                        </Button>
                      )}
                      {usuario.id !== user?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={usuario.papel === "coordenador" ? "w-full" : ""}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Excluir Usuário</AlertDialogTitle>
                              <AlertDialogDescription>
                                Tem certeza que deseja excluir o usuário "{usuario.nome}"? Esta ação não pode ser
                                desfeita.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteUsuario(usuario.id)}>
                                Excluir
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">Nenhum usuário cadastrado ainda.</p>
              </CardContent>
            </Card>
          )}
        </div>

        {personas.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <User className="w-6 h-6" />
              Personas Anteriores
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {personas.map((persona) => (
                <Card key={persona.id} className="hover:border-primary hover:shadow-lg transition-all">
                  <div
                    className="cursor-pointer"
                    onClick={() => navigate(`/treinamento?personaId=${persona.id}&empresaId=${empresa?.id}`)}
                  >
                    <CardHeader>
                      <div className="flex items-center justify-between gap-2">
                        <CardTitle className="text-sm">#{persona.id.toString().slice(-4)}</CardTitle>
                        <PersonaScoreBadge score={personaScores[persona.id]} />
                      </div>
                      <CardDescription>{new Date(persona.created_at).toLocaleString("pt-BR")}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm line-clamp-6">{persona.persona_gerada}</p>
                    </CardContent>
                  </div>
                  <CardContent className="pt-0">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="w-full">
                          <Trash2 className="h-4 w-4 mr-2 text-destructive" />
                          Excluir Persona
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Excluir Persona</AlertDialogTitle>
                          <AlertDialogDescription>
                            Tem certeza que deseja excluir esta persona? Esta ação não pode ser desfeita.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeletePersona(persona.id)}>Excluir</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Coordenador;
